import React from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet'

import StaticScene from '../StaticScene/StaticScene'

const TermsScene = () => (
  <StaticScene>
    <Helmet>
      <title>Terms of Use - {process.env.REACT_APP_SITE_TITLE}</title>
    </Helmet>

    <h1 className="mt-5">Terms of Use</h1>
    <p>Last Revised: April 14, 2017</p>
    <h3>Introduction</h3>
    <p>
      This website (this "Site") is provided to you by BallotReady, Inc. d/b/a
      CivicEngine ("CivicEngine" "we", "our", or "us") on behalf of the and
      hosted for the company represented on the Site ("Client"). These Terms of
      Service (the "Terms") are a legal agreement between you and CivicEngine,
      governing your access to and use of the Site and the online and offline
      services which CivicEngine agrees to provide to you in connection with the
      Site (the "Services") including providing you with publicly available
      information about political candidates and allowing you to view and make
      plans regarding upcoming election ballots.
    </p>
    <p>
      You represent and certify that you are of legal age to form a binding
      contract and are not a person barred from receiving Services under the
      laws of the United States or other applicable jurisdictions. To register
      as a user of the Services you must be 18 years or over. In the event that
      you are agreeing to these Terms on behalf of a third party entity, you
      represent and warrant that you have sufficient right to bind such third
      party to these Terms, in which case, all references to "you" in these
      Terms shall be references to such third party.
    </p>
    <p>
      BY AGREEING TO THESE TERMS, OR BY OTHERWISE ACCESSING THE SITE OR USING
      ANY OF THE SERVICES YOU AGREE TO BE BOUND BY THESE TERMS.
    </p>
    <p>
      In the case of inconsistencies between these Terms and information
      included in any other materials related to the Site or the Services (e.g.,
      promotional materials and mailers), these Terms will always govern and
      take precedence.
    </p>
    <h3>1. Services.</h3>
    <p>
      <strong>1.1. Services; Availability.</strong>
      CivicEngine uses reasonable efforts to ensure that the online features of
      the Services are available 24 hours a day, 7 days a week. However, there
      will be occasions when the Services will be interrupted for maintenance,
      upgrades and emergency repairs or due to failure of telecommunications
      links and equipment that are beyond the control of CivicEngine. We will
      use reasonable commercial efforts to minimize such disruption where it is
      within the reasonable control of CivicEngine. You agree that neither
      CivicEngine nor the Client will be liable to you for any unavailability,
      modification, suspension or discontinuance of the Services. You are
      responsible for obtaining access to any Services and that access may
      involve third party fees (such as Internet service provider or airtime
      charges). You are responsible for those fees, including those fees
      associated with the display or delivery of advertisements. In addition,
      you must provide and are responsible for all equipment necessary to access
      the Services.
    </p>
    <p>
      <strong>1.2. Registration.</strong>
      You may access certain online features of the Services through your
      account on the Site (your <strong>"Account"</strong>). You may log into
      your Account using a unique username and password. You are responsible for
      safeguarding your password and you agree not to disclose your password to
      any third party. You are solely responsible for any activities or actions
      taken under your username, whether or not you have authorized such
      activities or actions. You agree that the information that you provide to
      CivicEngine about yourself upon registration of your Account, and at all
      other times, (<strong>"Customer Data"</strong>) will be true, accurate,
      current and complete, and you further agree that you will maintain and
      promptly update the Customer Data to ensure that it remains true,
      accurate, current and complete. You may not impersonate any other person
      or use a username or password that you are not authorized to use.
      CivicEngine reserves the right to require you to change your password for
      any reason at any time. If you become aware of any unauthorized use of
      your Account, you agree to notify CivicEngine immediately at{' '}
      <a href={`mailto:${process.env.REACT_APP_EMAIL_DEFAULT_TO}`}>
        {process.env.REACT_APP_EMAIL_DEFAULT_TO}
      </a>
      .
    </p>
    <p>
      <strong>1.3. Site License.</strong>
      Subject to these Terms, CivicEngine grants you a personal, limited,
      revocable, non-exclusive and non-transferable license to access and use
      the Site and Services. This license is exclusive to you and you may not
      sublicense the use of the Site. CivicEngine expressly retains all
      ownership rights, title and interest in and to all aspects of the Services
      and the Site, including, but not limited to, all current and future
      patents, copyrights, trademarks, trade secrets, know-how, and other
      proprietary rights included or embodied in the Site. You may not modify
      the Site, create derivative works of the Site, or reverse engineer,
      reverse compile, reverse assemble or do any other operation with the Site
      that would reveal any source code, trade secrets, know-how or other
      proprietary information. This license shall not be construed or
      interpreted as granting or providing rights to you to use, reproduce,
      modify, distribute, perform, display, possess or control the source code
      or any other aspect of the Site. You may not remove or modify any notice
      of confidentiality, trade secret, trademark or copyright encoded or
      embodied in the Site or displayed by, on, or in the Site. You may use the
      Site only while these Terms remain in effect. Under no circumstances shall
      you have any rights of any kind in or to the Site after any termination or
      expiration of your agreement to these Terms for any reason.
    </p>
    <h3>2. Consent to Contact by Phone.</h3>
    <p>
      By providing your phone number, you authorize CivicEngine, the Client, and
      their respective partners to contact you, by voice call, text message, or
      otherwise at the telephone or mobile number provided even if your phone
      number is listed on any national or state "Do Not Call" list. Message and
      data rates may apply. You understand that consent is not a condition using
      the Service.
    </p>
    <h3>3. User Content and Submissions.</h3>
    <p>
      From time to time, we may present you with the opportunity to upload
      information, data, text, software, music, sound, photographs, graphics,
      video, messages, or other materials (<strong>"Content"</strong>) to the
      Site. CivicEngine does not claim ownership rights in any such Content that
      you make available, but by submitting your Content you grant CivicEngine a
      non-exclusive, perpetual, irrevocable, worldwide, fully-paid, transferable
      right and license, with the right to sublicense through multiple levels of
      sublicensees to reproduce, create derivative works of, distribute,
      publicly perform, publicly display, digitally transmit, and otherwise use
      the Content in any medium or format, whether now known or hereafter
      discovered. You represent and warrant to CivicEngine that you are the
      owner of the copyright to the Content or that you have written permission
      from the copyright owner to use such Content. In addition, you warrant
      that all moral rights in any Content have been waived. You agree to
      indemnify and hold CivicEngine harmless for any violation of this
      provision. While CivicEngine does not and cannot review all material on
      the Site, CivicEngine reserves the right to remove, delete, move, or edit
      Content that it, in its sole discretion, deems abusive, defamatory,
      obscene, in violation of the law, including but not limited to copyright
      or trademark law, or otherwise unacceptable. CivicEngine will not be
      liable for the Content of any submission.
    </p>
    <h3>4. Third Party Software and Linking.</h3>
    <p>
      Although we may make software, hyperlinks, and other products of
      third-party companies available to you, your use of such products is
      subject to the respective terms and conditions imposed by the third party
      owning, manufacturing or distributing such products, and the agreement for
      your use will be between you and such third party. CivicEngine makes no
      warranty with regard to the products or websites of any other entity.
      CivicEngine has no control over the content or availability of any
      third-party software or website. In particular, (a) CivicEngine makes no
      warranty that any third-party software you download or web site you visit
      will be free of any contaminating or destructive code, such as viruses,
      worms or Trojan horses and (b) it is your responsibility to become
      familiar with any web site's privacy and other policies and terms of
      service, and to contact that site's webmaster or site administrator with
      any concerns.
    </p>
    <h3>5. Suspension and Termination.</h3>
    <p>
      CivicEngine reserves the right to suspend or terminate your access to and
      use of the Site and Services, at any time, without notice, for any reason,
      including but not limited to (a) your breach of these Terms, or any other
      policies or guidelines set forth by CivicEngine, or (b) conduct that
      CivicEngine or the Client believes is harmful to other users of the Site
      or Services, or the business of CivicEngine or other third party
      information providers. Further, you agree that CivicEngine and the Client
      shall not be liable to you or any third party for any termination of your
      access to the Site or Services. CivicEngine reserves the right at any time
      and from time to time to modify or discontinue, temporarily or
      permanently, the Services (or any part thereof) with or without notice.
      You agree that CivicEngine or the Client shall not be liable to you or to
      any third party for any modification, suspension or discontinuance of the
      Services.
    </p>
    <h3>6. Privacy.</h3>
    <p>
      CivicEngine respects the privacy of is users. CivicEngine collects, uses
      and discloses information about you in accordance with the CivicEngine
      Privacy Policy, which can be viewed by clicking on the "Privacy Policy"
      link that appears at the bottom of each page of the Site or by visiting{' '}
      <Link to="/privacy">/privacy</Link> (<strong>"Privacy Policy"</strong>).
      You understand and agree that, notwithstanding any measures taken to
      prevent unauthorized disclosure, use of or connection to the Internet
      provides the opportunity for unauthorized third parties to circumvent such
      precautions and illegally gain access to confidential information.
      Accordingly, CivicEngine cannot and does not guarantee the privacy,
      security or authenticity of any information so transmitted over or stored
      in any system connected to the Internet.
    </p>
    <h3>7. Disclaimer of Warranty.</h3>
    <p>
      THE SERVICES AVAILABLE THROUGH THE SITE ARE PROVIDED ON AN "AS IS" AND "AS
      AVAILABLE" BASIS AND WITHOUT WARRANTIES OF ANY KIND. CIVICENGINE EXPRESSLY
      DISCLAIMS ALL WARRANTIES, WHETHER EXPRESS OR IMPLIED, WITH RESPECT TO THE
      SITE OR SERVICES, INCLUDING, BUT NOT LIMITED TO, IMPLIED WARRANTIES OF
      MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NON-INFRINGEMENT.
      CIVICENGINE MAKES NO WARRANTY THAT THE SITE OR THE SERVICES WILL BE
      UNINTERRUPTED, TIMELY, SECURE OR ERROR FREE, THAT THE SITE OR OUR SERVERS
      ARE FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS, THAT THE SITE, INCLUDING
      THE SERVICES WILL BE AVAILABLE, OR THAT DATA ARE SECURE FROM UNAUTHORIZED
      ACCESS. CIVICENGINE MAKES NO WARRANTY REGARDING ANY SOFTWARE, GOODS,
      SERVICES, PROMOTIONS, OR THE DELIVERY OF ANY GOODS OR SERVICES, PURCHASED,
      ACCESSED OR OBTAINED THROUGH THE SITE OR ADVERTISED THROUGH THE SITE. NO
      ADVICE OR INFORMATION GIVEN BY CIVICENGINE, ITS EMPLOYEES OR AFFILIATES
      SHALL CREATE A WARRANTY.
    </p>
    <h3>8. Rules of Conduct.</h3>
    <p>
      <strong>8.1. Distribution of Content.</strong>
      You agree that you will not distribute any Content that: (a) is
      defamatory, abusive, harassing, threatening, or an invasion of a right of
      privacy of another person; is bigoted, hateful, or racially or otherwise
      offensive; is violent, vulgar, obscene, pornographic or otherwise sexually
      explicit; or otherwise harms or can reasonably be expected to harm any
      person or entity; (b) is illegal or encourages or advocates illegal
      activity or the discussion of illegal activities with the intent to commit
      them, including a submission that is, or represents an attempt to engage
      in, child pornography, stalking, sexual assault, fraud, trafficking in
      obscene or stolen material, drug dealing and/or drug abuse, harassment,
      theft, or conspiracy to commit any criminal activity; (c) infringes or
      violates any right of a third party including any copyright, patent,
      trademark, trade secret or other proprietary or contractual rights, right
      of privacy (specifically, you must not distribute another person's
      personal information of any kind without their express permission) or
      publicity, or any confidentiality obligation; (d) is commercial,
      business-related or advertises or offers to sell any products, services or
      otherwise (whether or not for profit), or solicits others (including
      solicitations for contributions or donations); (e) contains a virus or
      other harmful component, or otherwise tampers with, impairs or damages the
      Site or any connected network, or otherwise interferes with any person or
      entity's use or enjoyment of the Site; (f) does not generally pertain to
      the designated topic or theme of the Site; (g) violates any specific
      restrictions applicable to a public forum, including its age restrictions
      and procedures; or (h) is antisocial, disruptive, or destructive,
      including "flaming," "spamming," "flooding," "trolling," and "griefing" as
      those terms are commonly understood and used on the Internet.
    </p>
    <p>
      <strong>8.2. Use of the Services.</strong>
      You expressly agree that you are solely responsible for any and all acts
      and omissions that occur under your Account or password, and you agree not
      to engage in any unacceptable uses of the Services, which include, without
      limitation, use of the Services to: (a) register for the Site and the
      Services if you have not acknowledged reading and agreed to abide by these
      Terms and the Privacy Policy; (b) interfere, disrupt or attempt to gain
      unauthorized access to other accounts on the Site or any other computer
      network; (c) use the Site or the Services in connection with any
      commercial endeavors without our prior written consent; (d) post your
      personal information such as instant messaging addresses, personal URLs,
      physical addresses and phone numbers in any publicly viewable areas of the
      Site; (e) post or give out any financial information or transmit
      electronically or physically any money to other users; (f) create user
      accounts by automated means or under fraudulent or false pretenses; (g)
      create or transmit unsolicited electronic communications such as spam to
      users or promote any products or services; (h) harass, threaten or
      intentionally embarrass or cause harm or distress to another person or
      group; (i) collect and publish any information about any of our users; (j)
      adapt, modify or reverse engineer any portion of the Services or the Site;
      (k) use any spider, robot, retrieval application, or any other device to
      retrieve any portion of the Site; (l) disseminate, store or transmit
      viruses, worms, trojan horses or other malicious code or program; (m)
      encourage conduct that would constitute a criminal or civil offense; (n)
      violate any applicable federal, state, local or international law or
      regulation; (o) exploit any person, including but not limited to
      exploiting any child under 18 years of age; (p) invade the privacy of any
      person, including but not limited to submitting personally identifying or
      otherwise private information about a person without their consent (or
      their parent's consent in the case of a child under 13 years of age); (q)
      solicit personal information from a child under 18 years of age; (r)
      submit false or misleading information to CivicEngine, the Site or other
      users; or (s) engage in any other activity deemed by CivicEngine to be in
      conflict with the spirit of these Terms and the Privacy Policy.
    </p>
    <p>
      <strong>8.3. Monitoring.</strong>
      We cannot and do not assure that other users are or will be complying with
      the foregoing Rules of Conduct or any other provisions of these Terms,
      and, as between you and us, you hereby assume all risk of harm or injury
      resulting from any such lack of compliance.
    </p>
    <h3>
      9. Proprietary and Privacy Protection for Other Users' Content on the
      Site.
    </h3>
    <p>
      CivicEngine hereby notifies you that all the information, content, image
      files, software and materials on the Site may be protected by U.S. and
      international copyright and other intellectual property laws and by other
      applicable laws, including privacy laws. CivicEngine is unable to provide
      you with permission to copy display or distribute material for which you
      do not own the copyright or other intellectual property rights. You may
      not copy or distribute such material without the written consent of the
      owner, and you are solely responsible for any copyright or other
      intellectual property law violations that you may incur as a result of
      your activities on the Site. CivicEngine has the absolute right to
      terminate your account or exclude you from the Site if you use our
      Services to violate the intellectual property rights or other rights of
      third parties. You agree to indemnify and hold CivicEngine harmless for
      any violation of this provision.
    </p>
    <h3>10. Indemnity; Limitation of Liability.</h3>
    <p>
      10.1. You agree to indemnify, and hold CivicEngine, the Client, and the
      officers, directors, employees and agents of each, harmless from and
      against any claims, liabilities, damages, losses, and expenses, including
      without limitation reasonable attorney's fees and costs, arising out of or
      in any way connected with (a) your access to or use of the Site or
      Services; (b) your violation of these Terms; (c) your violation of any
      third party right, including without limitation any intellectual property
      right or any publicity, confidentiality, property or privacy right; or (d)
      any claim that your use of the Site or Services caused damage to a third
      party.
    </p>
    <p>
      10.2. YOU ACKNOWLEDGE THAT YOUR ACCESS TO AND USE OF THE SITE OR SERVICES
      AND ANY RELIANCE UPON EITHER IS AT YOUR SOLE RISK, THAT YOU ASSUME FULL
      RESPONSIBILITY FOR ALL COSTS ASSOCIATED WITH ALL NECESSARY SERVICING OR
      REPAIRS OF ANY EQUIPMENT YOU USE IN CONNECTION WITH YOUR USE OF THE SITE
      OR SERVICES. YOU AGREE THAT, TO THE FULLEST EXTENT PERMITTED BY APPLICABLE
      LAW, UNDER NO CIRCUMSTANCES SHALL CIVICENGINE OR THE CLIENT BE LIABLE FOR
      ANY INDIRECT, INCIDENTAL, SPECIAL OR CONSEQUENTIAL DAMAGES, INCLUDING, BUT
      NOT LIMITED TO, DAMAGES FOR LOSS OF PROFITS, USE, IMAGES, DATA OR OTHER
      INTANGIBLES, EVEN IF CIVICENGINE OR THE CLIENT HAS BEEN ADVISED OF THE
      POSSIBILITY OF SUCH DAMAGES, THAT RESULT FROM THE USE OR THE INABILITY TO
      USE THE SITE OR THE SERVICES, FROM ANY CHANGES TO THE SITE OR THE SERVICES
      OR FROM UNAUTHORIZED ACCESS TO OR ALTERATION OF YOUR TRANSMISSIONS OR
      CONTENT. YOU SPECIFICALLY ACKNOWLEDGE THAT DOWN-TIME AND COMPUTER VIRUSES
      ARE RISKS INHERENT IN THE USE OF THE INTERNET AND SOFTWARE PRODUCTS, AND
      YOU AGREE TO ASSUME RESPONSIBILITY FOR ANY HARM OR DAMAGES OF ANY KIND OR
      CHARACTER WHATSOEVER RESULTING FROM THESE POSSIBLE HARMS. YOU ALSO
      SPECIFICALLY ACKNOWLEDGE THAT YOU MAY BE DISCLOSING SENSITIVE, PRIVATE AND
      CONFIDENTIAL INFORMATION ABOUT YOURSELF IN YOUR USE OF THE SITE AND THE
      SERVICES AND YOU AGREE TO ASSUME RESPONSIBILITY FOR ANY HARM OR DAMAGES OF
      ANY KIND OR CHARACTER WHATSOEVER RESULTING FROM YOUR RELEASE OF SUCH
      CONTENT.
    </p>
    <p>
      10.3. IF YOU ARE DISSATISFIED WITH THE SITE OR THE SERVICES OR WITH ANY OF
      THESE TERMS, OR FEEL CIVICENGINE HAS BREACHED THESE TERMS, YOUR SOLE AND
      EXCLUSIVE REMEDY IS TO DISCONTINUE USING THE SITE AND THE SERVICES. THE
      TOTAL LIABILITY OF CIVICENGINE TO YOU FOR ANY CLAIM ARISING FROM OR
      RELATING TO THESE TERMS OR USE OF THE SITE OR THE SERVICES SHALL NOT
      EXCEED THE AMOUNT PAID BY YOU FOR THE SERVICES, IF ANY. IT IS THE
      INTENTION OF YOU AND CIVICENGINE THAT THIS PROVISION BE CONSTRUED BY A
      COURT AS BEING THE BROADEST LIMITATION OF LIABILITY CONSISTENT WITH
      APPLICABLE LAW.
    </p>
    <p>
      10.4. SOME JURISDICTIONS DO NOT ALLOW THE LIMITATION OR EXCLUSION OF
      INCIDENTAL, CONSEQUENTIAL OR OTHER TYPES OF DAMAGES, SO SOME OF THE ABOVE
      LIMITATIONS MAY NOT APPLY TO YOU.
    </p>
    <h3>11. General.</h3>
    <p>
      <strong>11.1. Modification.</strong>
      CivicEngine may modify these Terms at any time. Modifications become
      effective immediately upon your first access to or use of the Site or
      Services after the "Last Revised" date at the top of these Terms. If you
      have created an Account, we shall notify you of any material changes to
      these Terms by email sent to the address you have provided to CivicEngine
      for your Account. Your continued access to or use of the Site or Services
      after the modifications have become effective will be deemed your
      conclusive acceptance of the modified Terms. If you do not agree with the
      modifications, do not access or use the Site or Services.
    </p>
    <p>
      <strong>11.2. Applicable Law and Dispute Resolution.</strong>
      These Terms shall be governed by the laws of the State of Illinois without
      giving effect to any conflict of laws principles that may require the
      application of the law of another jurisdiction. If you believe that
      CivicEngine has not adhered to these Terms, please contact us by e-mail at{' '}
      {process.env.REACT_APP_EMAIL_DEFAULT_TO}. We will do our best to address
      your concerns. If you feel that your complaint has been addressed
      incompletely, we invite you to let us know for further investigation. If
      you and CivicEngine are unable to reach a resolution to the dispute, you
      and CivicEngine will settle the dispute exclusively under the rules of the
      American Arbitration Association (www.adr.org) at its Chicago, Illinois
      office. Any election to arbitrate by one party will be final and binding
      on the other. YOU UNDERSTAND THAT IF EITHER PARTY ELECTS TO ARBITRATE,
      NEITHER PARTY WILL HAVE THE RIGHT TO SUE IN COURT OR HAVE A JURY TRIAL.
      You and CivicEngine agree that any arbitration will be limited to the
      dispute between CivicEngine and you individually. YOU ACKNOWLEDGE AND
      AGREE THAT YOU AND CIVICENGINE ARE EACH WAIVING THE RIGHT TO PARTICIPATE
      AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS ACTION OR
      REPRESENTATIVE PROCEEDING. Further, unless both you and CivicEngine
      otherwise agree in writing, an arbitrator may not consolidate more than
      one person's claims, and may not otherwise preside over any form of any
      class or representative proceeding. Regardless of any statute or law to
      the contrary, any claim or cause of action arising out of or related to
      the use of the Site or the Services or these Terms must be brought, if at
      all, within one year from the accrual of the claim or cause of action or
      be forever barred.
    </p>
    <p>
      <strong>11.3. No Resale of Services.</strong>
      You agree not to reproduce, duplicate, copy, sell, trade, resell or
      exploit for any commercial purposes, any portion of the Services, use of
      the Services, or access to the Services.
    </p>
    <p>
      <strong>11.4. Independent Contractors.</strong>
      No joint venture, partnership, employment, or agency relationship exists
      between you and CivicEngine as a result of these Terms or use of the Site
      or the Services.
    </p>
    <p>
      <strong>11.5. Enforcement.</strong>
      If any legal action is brought to enforce these Terms, the prevailing
      party will be entitled to reimbursement of its attorneys' fees, court
      costs, and other collection expenses, in addition to any other relief it
      may receive from the other party.
    </p>
    <p>
      <strong>11.6. Force Majeure.</strong>
      CivicEngine will not be liable by reason of any failure or delay in the
      performance of its obligations hereunder on account of strikes, shortages,
      riots, insurrection, fires, flood, storm, explosions, acts of God, war,
      governmental action, labor conditions, earthquakes, material shortages or
      any other cause which is beyond CivicEngine's reasonable control.
    </p>
    <p>
      <strong>11.7. Waiver.</strong>
      The failure of CivicEngine to enforce any right or provision in these
      Terms will not constitute a waiver of such right or provision unless
      acknowledged and agreed to by CivicEngine in writing.
    </p>
    <p>
      <strong>11.8. Construction.</strong>
      The headings of Sections of these Terms are for convenience and are not to
      be used in interpretation.
    </p>
    <p>
      <strong>11.9. Contact.</strong>
      CivicEngine is located in Chicago, Illinois, United States. Any questions,
      comments or suggestions, including any report of violation of these Terms
      should be provided to the Administrator as follows:
    </p>
    <p className="ml-3">
      By E-mail:{' '}
      <a href={`mailto:${process.env.REACT_APP_EMAIL_DEFAULT_TO}`}>
        {process.env.REACT_APP_EMAIL_DEFAULT_TO}
      </a>
    </p>
    <p className="ml-3">
      By Postal Mail: 1626 N. Honore St., Chicago, IL 60622
    </p>
    <p>
      <strong>11.10. Entire Agreement.</strong>
      These Terms constitute the entire agreement between you and CivicEngine
      and govern your use of the Site and the Services, superseding any prior
      agreements between you and CivicEngine. The failure of CivicEngine to
      exercise or enforce any right or provision of these Terms shall not
      constitute a waiver of such right or provision. If any provision of these
      Terms is found by a court of competent jurisdiction to be invalid, you and
      CivicEngine nevertheless agree that the court should endeavor to give
      effect to the parties' intentions as reflected in the provision, and the
      other provisions of these Terms shall remain in full force and effect.
    </p>
  </StaticScene>
)

export default TermsScene



// WEBPACK FOOTER //
// ./src/components/TermsScene/TermsScene.js